// Multi-platform OAuth Service
import api from '../config/api';

class OAuthService {
  // Start OAuth flow for any platform
  async startOAuth(platform, options = {}) {
    try {
      let endpoint;
      let payload = {
        brandId: options.brandId,
        reauth: options.reauth || false,
        connectorId: options.connectorId || null,
        country: options.country || null, // Add country parameter
        connectorName: options.connectorName || null, // Add connector name
        currency: options.currency || null // Add currency
      };

      // Optional admin app-switch (Facebook re-auth): re-mint the connector under a
      // chosen Meta app. Backend ignores it for every other platform / non-admin.
      if (options.targetAppKey) {
        payload.targetAppKey = options.targetAppKey;
      }

      switch (platform) {
        case 'shopify':
          endpoint = '/api/connectors/oauth/shopify/start';
          if (options.shopDomain) {
            payload.shopDomain = options.shopDomain;
          }
          break;
        
        case 'facebook':
          endpoint = '/api/connectors/oauth/facebook/start';
          break;
        
        case 'amazon_ads':
        case 'amazon-ads':
          endpoint = '/api/connectors/oauth/amazon-ads/start';
          break;
        
        case 'amazon':
        case 'amazon_sp':
        case 'amazon-sp':
          endpoint = '/api/connectors/oauth/amazon-sp/start';
          break;
        
        case 'google':
        case 'google_ads':
        case 'google-ads':
          endpoint = '/api/connectors/oauth/google-ads/start';
          break;
        
        case 'tiktok':
          endpoint = '/api/connectors/oauth/tiktok/start';
          break;

        case 'tiktok_business_account':
          endpoint = '/api/connectors/oauth/tiktok-business/start';
          break;

        case 'tiktok_creator':
        case 'tiktok-creator':
          endpoint = '/api/connectors/oauth/tiktok-creator/start';
          break;

        case 'slack':
          endpoint = '/api/connectors/oauth/slack/start';
          break;

        case 'microsoft_teams':
          endpoint = '/api/connectors/oauth/teams/start';
          break;

        case 'klaviyo':
          endpoint = '/api/connectors/oauth/klaviyo/start';
          break;
        
        case 'pinterest':
          endpoint = '/api/connectors/oauth/pinterest/start';
          break;
        
        case 'yotpo':
          endpoint = '/api/connectors/oauth/yotpo/start';
          break;
        
        case 'google_search_console':
        case 'google-search-console':
          endpoint = '/api/connectors/oauth/google-search-console/start';
          break;
        
        case 'google_analytics':
        case 'google-analytics':
          endpoint = '/api/connectors/oauth/google-analytics/start';
          break;

        case 'google_merchant_center':
        case 'google-merchant-center':
          endpoint = '/api/connectors/oauth/google-merchant-center/start';
          break;

        case 'google_postmaster':
        case 'google-postmaster':
          endpoint = '/api/connectors/oauth/google-postmaster/start';
          break;

        case 'quickbooks':
          endpoint = '/api/connectors/oauth/quickbooks/start';
          break;

        case 'xero':
          endpoint = '/api/connectors/oauth/xero/start';
          break;

        case 'microsoft_ads':
        case 'microsoft-ads':
          endpoint = '/api/connectors/oauth/microsoft-ads/start';
          break;

        case 'revolut':
          endpoint = '/api/connectors/oauth/revolut/start';
          // Revolut requires user-provided credentials
          if (options.clientId) {
            payload.clientId = options.clientId;
          }
          if (options.privateKey) {
            payload.privateKey = options.privateKey;
          }
          if (options.scopes) {
            payload.scopes = options.scopes;
          }
          if (options.useSandbox !== undefined) {
            payload.useSandbox = options.useSandbox;
          }
          break;

        case 'wise':
          endpoint = '/api/connectors/oauth/wise/start';
          // Wise uses standard OAuth (credentials in env variables)
          if (options.useSandbox !== undefined) {
            payload.useSandbox = options.useSandbox;
          }
          break;

        case 'snapchat_ads':
        case 'snapchat-ads':
          endpoint = '/api/connectors/oauth/snapchat-ads/start';
          break;

        case 'reddit_ads':
        case 'reddit-ads':
          endpoint = '/api/connectors/oauth/reddit-ads/start';
          break;

        case 'sa360':
          endpoint = '/api/connectors/oauth/sa360/start';
          break;

        case 'amazon_vendor':
        case 'amazon-vendor':
          endpoint = '/api/connectors/oauth/amazon-vendor/start';
          break;

        case 'dv360':
          endpoint = '/api/connectors/oauth/dv360/start';
          break;

        case 'cm360':
          endpoint = '/api/connectors/oauth/cm360/start';
          break;

        case 'square':
          endpoint = '/api/connectors/oauth/square/start';
          if (options.useSandbox !== undefined) {
            payload.useSandbox = options.useSandbox;
          }
          break;

        case 'paypal':
          endpoint = '/api/connectors/oauth/paypal/start';
          // PayPal Transaction Search API
          if (options.useSandbox !== undefined) {
            payload.useSandbox = options.useSandbox;
          }
          break;

        case 'payoneer':
          endpoint = '/api/connectors/oauth/payoneer/start';
          // Payoneer PSD2 API
          if (options.useSandbox !== undefined) {
            payload.useSandbox = options.useSandbox;
          }
          break;

        case 'attentive':
          endpoint = '/api/connectors/oauth/attentive/start';
          break;

        case 'tiktok_shop':
        case 'tiktok-shop':
          endpoint = '/api/connectors/oauth/tiktok-shop/start';
          // TikTok Shop US is a separate app/portal (and EU a client-owned app) — pick the
          // authorize host + app credentials on the backend based on region. Default 'GLOBAL' (UK/EU/SEA).
          payload.region = ['US', 'EU'].includes(options.region) ? options.region : 'GLOBAL';
          break;

        case 'google_workspace':
        case 'google-workspace':
          endpoint = '/api/connectors/oauth/google-workspace/start';
          if (options.type) {
            payload.type = options.type;
          } else {
            payload.type = 'company'; // default to company-level
          }
          break;

        case 'microsoft_workspace':
        case 'microsoft-workspace':
          endpoint = '/api/connectors/oauth/microsoft-workspace/start';
          if (options.type) {
            payload.type = options.type;
          } else {
            payload.type = 'company';
          }
          break;

        case 'sps_commerce':
        case 'sps-commerce':
          endpoint = '/api/connectors/oauth/sps-commerce/start';
          break;

        default:
          throw new Error(`OAuth not supported for platform: ${platform}`);
      }

      // Log the request for debugging
      console.log(`Starting ${options.reauth ? 're-authentication' : 'authentication'} for ${platform}:`, payload);
      
      const response = await api.post(endpoint, payload);
      
      if (response.data.success) {
        // Redirect to OAuth URL
        window.location.href = response.data.authUrl;
      } else {
        throw new Error(response.data.message || 'OAuth failed to start');
      }
    } catch (error) {
      console.error(`Error starting ${platform} OAuth:`, error);
      throw new Error(error.response?.data?.message || error.message || `Failed to start ${platform} OAuth`);
    }
  }

  // Platforms whose authorize link can be EMAILED to an account owner who has no
  // SuperChargeMe login. Requirement for being on this list: the platform's OAuth callback
  // is public AND provisions the connector itself (no login-gated completion step), and its
  // /start endpoint returns the authorize URL instead of redirecting.
  //   - tiktok_shop : state TTL already defaults to 24h (TIKTOK_SHOP_STATE_TTL_HOURS)
  //   - amazon (SP) + amazon_ads : pass `shareable: true` so the backend uses the 24h
  //     share TTL instead of the 10-minute in-app window (AMAZON_OAUTH_SHARE_TTL_HOURS)
  static get SHAREABLE_PLATFORMS() {
    return {
      'tiktok_shop': { endpoint: '/api/connectors/oauth/tiktok-shop/start', kind: 'tiktok' },
      'tiktok-shop': { endpoint: '/api/connectors/oauth/tiktok-shop/start', kind: 'tiktok' },
      // TikTok Ads (Marketing API, platform 8). Its /start reads only brandId; the shared `kind:
      // 'tiktok'` payload also sends `region`, which the TikTok Ads handler ignores (harmless).
      // State TTL defaults to 24h server-side (TIKTOK_ADS_STATE_TTL_HOURS).
      'tiktok': { endpoint: '/api/connectors/oauth/tiktok/start', kind: 'tiktok' },
      'tiktok_ads': { endpoint: '/api/connectors/oauth/tiktok/start', kind: 'tiktok' },
      // TikTok Business Account / organic (platform 25). Its /start reads only brandId; the shared
      // `kind: 'tiktok'` payload also sends `region`, which the handler ignores (harmless). State
      // TTL defaults to 24h server-side (TIKTOK_BUSINESS_STATE_TTL_HOURS); the public callback
      // provisions the connector itself and returns shared_link:true for the "close this tab" UX.
      'tiktok_business_account': { endpoint: '/api/connectors/oauth/tiktok-business/start', kind: 'tiktok' },
      'tiktok-business': { endpoint: '/api/connectors/oauth/tiktok-business/start', kind: 'tiktok' },
      'amazon': { endpoint: '/api/connectors/oauth/amazon-sp/start', kind: 'amazon' },
      'amazon_sp': { endpoint: '/api/connectors/oauth/amazon-sp/start', kind: 'amazon' },
      'amazon-sp': { endpoint: '/api/connectors/oauth/amazon-sp/start', kind: 'amazon' },
      'amazon_ads': { endpoint: '/api/connectors/oauth/amazon-ads/start', kind: 'amazon' },
      'amazon-ads': { endpoint: '/api/connectors/oauth/amazon-ads/start', kind: 'amazon' },
    };
  }

  // Fetch a shareable authorize URL WITHOUT redirecting — powers the "Copy client link"
  // button. An internal user copies this and sends it to a client who is NOT logged into
  // SuperChargeMe; the client authorizes with the platform and the public callback
  // auto-provisions the connector. Returns the SAME authUrl that startOAuth would redirect
  // to, so the link format is identical to the one the platform already accepts.
  // Returns { authUrl, ttlHours } — ttlHours is what the backend actually applied (may be
  // undefined for tiktok_shop, whose /start does not report it).
  async getShareableLink(platform, options = {}) {
    const config = OAuthService.SHAREABLE_PLATFORMS[platform];
    if (!config) {
      throw new Error(`Shareable link is not supported for platform: ${platform}`);
    }
    if (!options.brandId) {
      throw new Error('A brand must be selected before generating a client link');
    }
    let payload;
    if (config.kind === 'tiktok') {
      // Unchanged from the shipped TikTok Shop flow — same fields, same order.
      payload = {
        brandId: options.brandId,
        region: ['US', 'EU'].includes(options.region) ? options.region : 'GLOBAL',
      };
    } else {
      // Amazon (both connectors) needs the marketplace/country to build the region-specific
      // consent URL, exactly as the in-app Connect button does.
      if (!options.country) {
        throw new Error('A country/region must be selected before generating a client link');
      }
      payload = {
        brandId: options.brandId,
        country: options.country,
        connectorName: options.connectorName,
        currency: options.currency || 'USD',
        shareable: true,
      };
    }
    let response;
    try {
      response = await api.post(config.endpoint, payload);
    } catch (err) {
      // Surface the backend's reason (e.g. a 403 tenant refusal) instead of axios's
      // "Request failed with status code 403".
      throw new Error(err.response?.data?.message || err.message || 'Failed to generate client link');
    }
    if (response.data && response.data.success && response.data.authUrl) {
      return { authUrl: response.data.authUrl, ttlHours: response.data.linkTtlHours };
    }
    throw new Error(response.data?.message || 'Failed to generate client link');
  }


  // Handle OAuth callback (called by the callback component)
  async handleCallback(platform, params) {
    try {
      let endpoint;
      
      switch (platform) {
        case 'shopify':
          endpoint = '/api/connectors/oauth/shopify/callback';
          break;
        case 'facebook':
          endpoint = '/api/connectors/oauth/facebook/callback';
          break;
        case 'amazon_ads':
        case 'amazon-ads':
          endpoint = '/api/connectors/oauth/amazon-ads/callback';
          break;
        case 'amazon-sp':
          endpoint = '/api/connectors/oauth/amazon-sp/callback';
          break;
        case 'google':
        case 'google_ads':
        case 'google-ads':
          endpoint = '/api/connectors/oauth/google-ads/callback';
          break;
        case 'tiktok':
          endpoint = '/api/connectors/oauth/tiktok/callback';
          break;
        case 'tiktok_business_account':
          endpoint = '/api/connectors/oauth/tiktok-business/callback';
          break;
        case 'tiktok_creator':
        case 'tiktok-creator':
          endpoint = '/api/connectors/oauth/tiktok-creator/callback';
          break;
        case 'slack':
          endpoint = '/api/connectors/oauth/slack/callback';
          break;
        case 'microsoft_teams':
        case 'teams':
          endpoint = '/api/connectors/oauth/teams/callback';
          break;
        case 'klaviyo':
          endpoint = '/api/connectors/oauth/klaviyo/callback';
          break;
        case 'pinterest':
          endpoint = '/api/connectors/oauth/pinterest/callback';
          break;
        case 'yotpo':
          endpoint = '/api/connectors/oauth/yotpo/callback';
          break;
        case 'google_search_console':
        case 'google-search-console':
          endpoint = '/api/connectors/oauth/google-search-console/callback';
          break;
        case 'google_analytics':
        case 'google-analytics':
          endpoint = '/api/connectors/oauth/google-analytics/callback';
          break;
        case 'google_merchant_center':
        case 'google-merchant-center':
          endpoint = '/api/connectors/oauth/google-merchant-center/callback';
          break;
        case 'google_postmaster':
        case 'google-postmaster':
          endpoint = '/api/connectors/oauth/google-postmaster/callback';
          break;
        case 'quickbooks':
          endpoint = '/api/connectors/oauth/quickbooks/callback';
          break;
        case 'xero':
          endpoint = '/api/connectors/oauth/xero/callback';
          break;
        case 'microsoft_ads':
        case 'microsoft-ads':
          endpoint = '/api/connectors/oauth/microsoft-ads/callback';
          break;
        case 'revolut':
          endpoint = '/api/connectors/oauth/revolut/callback';
          break;
        case 'wise':
          endpoint = '/api/connectors/oauth/wise/callback';
          break;
        case 'paypal':
          endpoint = '/api/connectors/oauth/paypal/callback';
          break;
        case 'payoneer':
          endpoint = '/api/connectors/oauth/payoneer/callback';
          break;
        case 'attentive':
          endpoint = '/api/connectors/oauth/attentive/callback';
          break;
        case 'tiktok_shop':
        case 'tiktok-shop':
        case 'tiktokshop': // non-hyphen alias — Partner Center redirect path
          endpoint = '/api/connectors/oauth/tiktok-shop/callback';
          break;
        case 'google_workspace':
        case 'google-workspace':
          endpoint = '/api/connectors/oauth/google-workspace/callback';
          break;
        case 'microsoft_workspace':
        case 'microsoft-workspace':
          endpoint = '/api/connectors/oauth/microsoft-workspace/callback';
          break;
        case 'sps_commerce':
        case 'sps-commerce':
          endpoint = '/api/connectors/oauth/sps-commerce/callback';
          break;
        case 'lightspeed': // Lightspeed Retail (R-Series) OAuth — /oauth/lightspeed/callback
          endpoint = '/api/connectors/oauth/lightspeed/callback';
          break;
        case 'square':
          endpoint = '/api/connectors/oauth/square/callback';
          break;
        case 'business-central': // Microsoft Dynamics 365 Business Central — /oauth/business-central/callback
          endpoint = '/api/connectors/oauth/business-central/callback';
          break;
        case 'snapchat-ads':
          endpoint = '/api/connectors/oauth/snapchat-ads/callback';
          break;
        case 'reddit-ads': // Reddit Ads — /oauth/reddit-ads/callback
          endpoint = '/api/connectors/oauth/reddit-ads/callback';
          break;
        case 'sa360': // Search Ads 360 — /oauth/sa360/callback
          endpoint = '/api/connectors/oauth/sa360/callback';
          break;
        case 'amazon-vendor': // Amazon Vendor Central — /oauth/amazon-vendor/callback
          endpoint = '/api/connectors/oauth/amazon-vendor/callback';
          break;
        case 'dv360': // Display & Video 360 — /oauth/dv360/callback
          endpoint = '/api/connectors/oauth/dv360/callback';
          break;
        case 'cm360': // Campaign Manager 360 — /oauth/cm360/callback
          endpoint = '/api/connectors/oauth/cm360/callback';
          break;
        default:
          throw new Error(`OAuth callback not supported for platform: ${platform}`);
      }

      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error) {
      console.error(`Error handling ${platform} OAuth callback:`, error);
      throw new Error(error.response?.data?.message || error.message || `Failed to complete ${platform} OAuth`);
    }
  }

  // Get platform display name
  getPlatformName(platform) {
    const names = {
      'shopify': 'Shopify',
      'facebook': 'Meta Ads',
      'amazon_ads': 'Amazon Ads',
      'amazon-ads': 'Amazon Ads',
      'amazon-sp': 'Amazon Seller Partner',
      'google': 'Google Ads',
      'google_ads': 'Google Ads',
      'google-ads': 'Google Ads',
      'tiktok': 'TikTok Ads',
      'tiktok_business_account': 'TikTok Business Account',
      'tiktok_creator': 'TikTok Creator',
      'tiktok-creator': 'TikTok Creator',
      'slack': 'Slack',
      'microsoft_teams': 'Microsoft Teams',
      'teams': 'Microsoft Teams',
      'klaviyo': 'Klaviyo',
      'pinterest': 'Pinterest Business',
      'google_search_console': 'Google Search Console',
      'google-search-console': 'Google Search Console',
      'google_analytics': 'Google Analytics',
      'google-analytics': 'Google Analytics',
      'google_merchant_center': 'Google Merchant Center',
      'google-merchant-center': 'Google Merchant Center',
      'google_postmaster': 'Google Postmaster',
      'google-postmaster': 'Google Postmaster',
      'quickbooks': 'QuickBooks Online',
      'xero': 'Xero Accounting',
      'microsoft_ads': 'Microsoft Advertising',
      'microsoft-ads': 'Microsoft Advertising',
      'yotpo': 'Yotpo SMS',
      'revolut': 'Revolut Business',
      'lightspeed': 'Lightspeed Retail',
      'square': 'Square',
      'business-central': 'Business Central',
      'business_central': 'Business Central',
      'snapchat_ads': 'Snapchat Ads',
      'snapchat-ads': 'Snapchat Ads',
      'reddit_ads': 'Reddit Ads',
      'reddit-ads': 'Reddit Ads',
      'sa360': 'Search Ads 360',
      'amazon_vendor': 'Amazon Vendor Central',
      'amazon-vendor': 'Amazon Vendor Central',
      'dv360': 'Display & Video 360',
      'cm360': 'Campaign Manager 360',
      'wise': 'Wise (TransferWise)',
      'paypal': 'PayPal Business',
      'payoneer': 'Payoneer Business',
      'twilio': 'Twilio',
      'attentive': 'Attentive',
      'tiktok_shop': 'TikTok Shop',
      'tiktok-shop': 'TikTok Shop',
      'tiktokshop': 'TikTok Shop',
      'google_workspace': 'Google Workspace',
      'google-workspace': 'Google Workspace',
      'microsoft_workspace': 'Microsoft 365 Workspace',
      'microsoft-workspace': 'Microsoft 365 Workspace',
      'sps_commerce': 'SPS Commerce EDI',
      'sps-commerce': 'SPS Commerce EDI'
    };
    return names[platform] || platform;
  }
}

export const oauthService = new OAuthService();
export default oauthService;
